// Cliente das rotas de site do alfredo-central-backend.
//
// O Central é o EDITOR e o dono do dado; quem renderiza `<handle>.alfredo.food`
// para o cliente final é o alfredo-pedidos, que lê a rota pública do mesmo
// backend. O que esta classe fala é só a parte autenticada.
import api from "./api.service";
import { EscopoDoSite, TemaDoSite } from "../comum/site";

export interface DescritorServido {
  tipo: string;
  rotulo: string;
  descricao: string;
  escopos: EscopoDoSite[];
  maximoPorPagina: number;
  campos: any[];
  padrao: Record<string, unknown>;
}

class SiteClass {
  /**
   * O catálogo de blocos, servido pelo backend a partir do MESMO objeto que
   * valida a gravação. Não há cópia da lista aqui de propósito: é o que impede
   * o editor de oferecer um campo que o servidor recusa.
   */
  async catalogo(): Promise<{ blocos: DescritorServido[] }> {
    const resposta = await api.get("/site/catalogo");
    return resposta.data;
  }

  /** Handle vigente, aposentados, tema, páginas e unidades de um escopo. */
  async buscar(escopo: EscopoDoSite, escopoUuid: string) {
    const resposta = await api.get(`/site/${escopo}/${escopoUuid}`);
    return resposta.data;
  }

  async definirHandle(escopo: EscopoDoSite, escopoUuid: string, handle: string) {
    const resposta = await api.put(`/site/${escopo}/${escopoUuid}/handle`, {
      handle,
    });
    return resposta.data;
  }

  async salvarTema(escopo: EscopoDoSite, escopoUuid: string, tema: TemaDoSite) {
    const resposta = await api.put(`/site/${escopo}/${escopoUuid}/tema`, { tema });
    return resposta.data;
  }

  /** O RASCUNHO da página, com os blocos. Página nova vem com a landing padrão. */
  async buscarPagina(escopo: EscopoDoSite, escopoUuid: string, caminho = "/") {
    const resposta = await api.get(
      `/site/${escopo}/${escopoUuid}/pagina?caminho=${encodeURIComponent(caminho)}`
    );
    return resposta.data;
  }

  async salvarPagina(
    escopo: EscopoDoSite,
    escopoUuid: string,
    paginaUuid: string,
    blocos: { tipo: string; config: unknown }[]
  ) {
    const resposta = await api.put(`/site/${escopo}/${escopoUuid}/pagina`, {
      paginaUuid,
      blocos,
    });
    return resposta.data;
  }

  /** Publicar é explícito: salvar rascunho NÃO muda o que o cliente vê. */
  async publicar(
    escopo: EscopoDoSite,
    escopoUuid: string,
    paginaUuid: string,
    rotulo?: string
  ) {
    const resposta = await api.post(`/site/${escopo}/${escopoUuid}/publicar`, {
      paginaUuid,
      rotulo,
    });
    return resposta.data;
  }

  /** Cria uma página com caminho próprio (`/sobre`). Idempotente no servidor. */
  async criarPagina(escopo: EscopoDoSite, escopoUuid: string, caminho: string) {
    const resposta = await api.post(`/site/${escopo}/${escopoUuid}/pagina`, { caminho });
    return resposta.data;
  }

  /**
   * Título, descrição e imagem de OG — o que o dono ESCREVE, e o que o Google e
   * o WhatsApp mostram. Campo vazio volta a derivar do cadastro.
   */
  async atualizarPagina(
    escopo: EscopoDoSite,
    escopoUuid: string,
    paginaUuid: string,
    dados: { titulo?: string; descricao?: string | null; imagemOg?: string | null }
  ) {
    const resposta = await api.patch(
      `/site/${escopo}/${escopoUuid}/pagina/${paginaUuid}`,
      dados
    );
    return resposta.data;
  }

  /** Tira a página do ar sem apagar nada. A home (`/`) não pode ser arquivada. */
  async arquivarPagina(
    escopo: EscopoDoSite,
    escopoUuid: string,
    paginaUuid: string,
    arquivada: boolean
  ) {
    const resposta = await api.post(
      `/site/${escopo}/${escopoUuid}/pagina/${paginaUuid}/arquivar`,
      { arquivada }
    );
    return resposta.data;
  }

  /** Quem trocou o endereço público, quando, de quê para quê. */
  async auditoriaDeEndereco(escopo: EscopoDoSite, escopoUuid: string) {
    const resposta = await api.get(`/site/${escopo}/${escopoUuid}/auditoria`);
    return resposta.data;
  }

  async restaurar(escopo: EscopoDoSite, escopoUuid: string, versaoUuid: string) {
    const resposta = await api.post(`/site/${escopo}/${escopoUuid}/restaurar`, {
      versaoUuid,
    });
    return resposta.data;
  }

  async buscarUnidade(subsidiaryUuid: string) {
    const resposta = await api.get(`/site/unidade/${subsidiaryUuid}`);
    return resposta.data;
  }

  /** Balcões, usuários e tipos de caixa do PDV, para o dono ESCOLHER. */
  async opcoesDaUnidade(subsidiaryUuid: string) {
    const resposta = await api.get(`/site/unidade/${subsidiaryUuid}/opcoes`);
    return resposta.data;
  }

  async salvarUnidade(
    subsidiaryUuid: string,
    dados: {
      coordenada?: unknown;
      contato?: unknown;
      operacional?: {
        aceitaPedido?: boolean;
        periodTypeUuid?: string | null;
        balcaoUuid?: string | null;
        usuarioUuid?: string | null;
      };
    }
  ) {
    const resposta = await api.put(`/site/unidade/${subsidiaryUuid}`, dados);
    return resposta.data;
  }
}

export const siteService = new SiteClass();
